import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { toast } from "react-hot-toast";
import { fetchUnifiedCallLogs } from "js/Api/devices";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopy from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PhoneIcon from "@mui/icons-material/Phone";
import VideocamIcon from "@mui/icons-material/Videocam";
import { getMediaSignUrl } from "js/Api/devices";
import { Button } from '../../../../Component/CommonFormEle';
import { hasValue } from "../../../../helpers/common_functions";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ─── Transcript viewer (video calls only) ────────────────────────────────────

// Parses a VTT timestamp string (any number of hour digits, e.g. Agora's "491256:34:45.156")
// into total seconds as a float.
const vttTimestampToSeconds = (ts) => {
    if (!ts) return 0;
    const parts = ts.replace(",", ".").split(":");
    if (parts.length === 3) {
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const s = parseFloat(parts[2]);
        return h * 3600 + m * 60 + s;
    }
    return 0;
};

// Formats elapsed seconds into "MM:SS" or "H:MM:SS".
const formatElapsedSeconds = (totalSeconds) => {
    const s = Math.floor(totalSeconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

// Parses a single VTT text into an array of cues, each with timestamp + speaker + text.
// Every cue is kept as its own entry — nothing is merged.
const parseVTTCues = (vttText) => {
    const cues = [];
    const lines = vttText.split("\n");
    let currentCue = null;

    for (const line of lines) {
        const trimmed = line.trim();

        if (trimmed === "" || trimmed === "WEBVTT") {
            if (currentCue?.text) { cues.push(currentCue); currentCue = null; }
            continue;
        }

        // Allow any number of digits for hours — Agora uses large session-clock values
        const tsMatch = trimmed.match(/^(\d+:\d{2}:\d{2}[.,]\d+)\s*-->\s*(\d+:\d{2}:\d{2}[.,]\d+)/);
        if (tsMatch) {
            if (currentCue?.text) cues.push(currentCue);
            currentCue = {
                startTime: tsMatch[1].replace(",", "."),
                endTime:   tsMatch[2].replace(",", "."),
                startSeconds: vttTimestampToSeconds(tsMatch[1]),
                speakerId: null,
                text: "",
            };
            continue;
        }

        if (currentCue) {
            // Skip pure cue-identifier lines (numeric or UUID-like, no spaces)
            if (/^[\w-]+$/.test(trimmed) && !trimmed.includes(" ") && !trimmed.startsWith("<")) continue;

            let textContent = trimmed;
            // Agora speaker tag: <v 123456>text</v>  or  <v 123456>text
            const speakerMatch = textContent.match(/^<v\s+([^>]+)>(.*)/);
            if (speakerMatch) {
                currentCue.speakerId = speakerMatch[1].trim();
                textContent = speakerMatch[2];
            }
            textContent = textContent.replace(/<[^>]*>/g, "").trim();
            if (textContent) currentCue.text += (currentCue.text ? " " : "") + textContent;
        }
    }

    if (currentCue?.text) cues.push(currentCue);
    return cues;
};

const ConversationDisplay = ({ vttUrl, participants = [] }) => {
    const [cues, setCues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Build a fallback mapping: for any speakerId that doesn't match a known
    // agoraUserId, assign names in the order speakers first appear in the transcript.
    const speakerNameMap = useMemo(() => {
        const map = {};
        const namedParticipants = participants.filter((p) => p.name);
        let fallbackIndex = 0;
        cues.forEach((c) => {
            if (!c.speakerId || c.speakerId in map) return;
            const match = namedParticipants.find(
                (p) => p.agoraUserId != null && String(p.agoraUserId) === String(c.speakerId)
            );
            if (match) {
                map[c.speakerId] = match.name;
            } else if (fallbackIndex < namedParticipants.length) {
                map[c.speakerId] = namedParticipants[fallbackIndex++].name;
            } else {
                map[c.speakerId] = `Speaker ${Object.keys(map).length + 1}`;
            }
        });
        return map;
    }, [cues, participants]);

    const getParticipantName = (speakerId) => {
        if (!speakerId) return "Unknown";
        return speakerNameMap[speakerId] || `Speaker ${speakerId}`;
    };

    // Assign a stable colour per unique speaker for visual separation.
    const speakerColors = useMemo(() => {
        const palette = ["#4a90d9", "#e67e22", "#27ae60", "#8e44ad", "#c0392b", "#16a085"];
        const map = {};
        let i = 0;
        cues.forEach((c) => {
            if (c.speakerId && !(c.speakerId in map)) {
                map[c.speakerId] = palette[i % palette.length];
                i++;
            }
        });
        return map;
    }, [cues]);

    useEffect(() => {
        if (!vttUrl) return;
        const controller = new AbortController();
        const { signal } = controller;

        const fetchAndParse = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(vttUrl, { signal });
                if (!response.ok) throw new Error("Failed to load transcript");

                const content = await response.text();
                let allCues = [];

                if (content.includes("#EXTM3U")) {
                    const baseUrlPath = vttUrl.substring(0, vttUrl.lastIndexOf("/") + 1);
                    const segmentLines = content.split("\n")
                        .map((l) => l.trim())
                        .filter((l) => l && !l.startsWith("#"));

                    for (const seg of segmentLines) {
                        if (signal.aborted) return;
                        const fileUrl = seg.startsWith("http") ? seg : baseUrlPath + seg;
                        try {
                            const segRes = await fetch(fileUrl, { signal });
                            if (!segRes.ok) continue;
                            const segText = await segRes.text();
                            allCues = allCues.concat(parseVTTCues(segText));
                        } catch { /* skip bad segment */ }
                    }
                } else if (content.includes("WEBVTT")) {
                    allCues = parseVTTCues(content);
                }

                if (!signal.aborted) setCues(allCues);
            } catch (err) {
                if (err.name !== "AbortError") setError(err.message);
            } finally {
                if (!signal.aborted) setLoading(false);
            }
        };

        fetchAndParse();
        return () => controller.abort();
    }, [vttUrl]);

    if (loading)
        return <div style={{ padding: "20px", textAlign: "center", color: "#6c757d" }}>Loading transcript...</div>;
    if (error)
        return <div style={{ padding: "15px", backgroundColor: "#f8d7da", color: "#721c24", borderRadius: "4px" }}>Error: {error}</div>;
    if (cues.length === 0)
        return <div style={{ padding: "30px", textAlign: "center", color: "#6c757d", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>No conversation data available</div>;

    // Compute elapsed time relative to the first cue so Agora's session-clock
    // timestamps (e.g. "491256:34:45") display as meaningful elapsed "MM:SS".
    const baseSeconds = cues.length > 0 ? (cues[0].startSeconds ?? 0) : 0;

    return (
        <div style={{ fontSize: "14px", lineHeight: "1.5" }} id="divTranscriptionData">
            {cues.map((cue, index) => {
                const name    = getParticipantName(cue.speakerId);
                const color   = speakerColors[cue.speakerId] || "#555";
                const elapsed = formatElapsedSeconds((cue.startSeconds ?? 0) - baseSeconds);
                return (
                    <div key={index} style={{ display: "flex", gap: "12px", marginBottom: "14px", alignItems: "flex-start" }}>
                        {/* Elapsed time */}
                        <div style={{ minWidth: "42px", fontSize: "11px", color: "#aaa", paddingTop: "3px", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                            {elapsed}
                        </div>
                        {/* Bubble */}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: "11px", fontWeight: "600", color, marginBottom: "3px" }}>
                                {name}
                            </div>
                            <div style={{ backgroundColor: "#f4f5f7", borderRadius: "8px", padding: "8px 12px", color: "#333", fontSize: "14px" }}>
                                {cue.text}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ─── VTT helpers (video calls only) ──────────────────────────────────────────

const resolveTranscriptTextContent = async (vttUrl) => {
    const response = await fetch(vttUrl);
    if (!response.ok) throw new Error("Failed to load transcript");

    const content = await response.text();

    if (content.includes("#EXTM3U")) {
        const baseUrlPath = vttUrl.substring(0, vttUrl.lastIndexOf("/") + 1);
        const segmentLines = content.split("\n").map((l) => l.trim()).filter((l) => l && !l.startsWith("#"));
        if (segmentLines.length === 0) throw new Error("No transcript file found in playlist");
        // Fetch all segments and concatenate their raw VTT text
        let merged = "WEBVTT\n\n";
        for (const seg of segmentLines) {
            const fileUrl = seg.startsWith("http") ? seg : baseUrlPath + seg;
            try {
                const segRes = await fetch(fileUrl);
                if (!segRes.ok) continue;
                const segText = await segRes.text();
                // Strip the WEBVTT header from every segment before appending
                const body = segText.replace(/^WEBVTT[^\n]*\n+/, "");
                merged += body + "\n";
            } catch { /* skip bad segment */ }
        }
        return merged;
    }

    return content;
};

const convertVttToReadableText = (vttText, participants = []) => {
    const cues = parseVTTCues(vttText);
    const namedParticipants = participants.filter((p) => p.name);
    const speakerNameMap = {};
    let fallbackIndex = 0;
    cues.forEach((c) => {
        if (!c.speakerId || c.speakerId in speakerNameMap) return;
        const match = namedParticipants.find(
            (p) => p.agoraUserId != null && String(p.agoraUserId) === String(c.speakerId)
        );
        if (match) {
            speakerNameMap[c.speakerId] = match.name;
        } else if (fallbackIndex < namedParticipants.length) {
            speakerNameMap[c.speakerId] = namedParticipants[fallbackIndex++].name;
        } else {
            speakerNameMap[c.speakerId] = `Speaker ${Object.keys(speakerNameMap).length + 1}`;
        }
    });
    const getName = (speakerId) => speakerId ? (speakerNameMap[speakerId] || `Speaker ${speakerId}`) : "Unknown";
    const baseSeconds = cues.length > 0 ? (cues[0].startSeconds ?? 0) : 0;
    return cues
        .map((c) => `[${formatElapsedSeconds((c.startSeconds ?? 0) - baseSeconds)}] ${getName(c.speakerId)}: ${c.text}`)
        .join("\n");
};

// ─── Unified category badge ───────────────────────────────────────────────────

// Normalises a raw phone outcome into a display label.
// cancelled / failed / busy all collapse to "blocked".
const normalizePhoneOutcome = (outcome) => {
    if (!outcome) return "unknown";
    const o = outcome.toLowerCase();
    if (o === "cancelled" || o === "failed" || o === "busy") return "blocked";
    return o;
};

// Maps any category label (phone outcome or video category) to badge colours.
const categoryStyles = {
    // phone outcomes
    connected:      { backgroundColor: "#d4edda", color: "#155724" },
    missed:         { backgroundColor: "#fff3cd", color: "#856404" },
    declined:       { backgroundColor: "#f8d7da", color: "#721c24" },
    blocked:        { backgroundColor: "#e2e3e5", color: "#383d41" },
    // video categories — "missed call" shares the same yellow as phone missed
    "missed call":  { backgroundColor: "#fff3cd", color: "#856404" },
    "dashboard call": { backgroundColor: "#d1ecf1", color: "#0c5460" },
    "family call":  { backgroundColor: "#e8d4f5", color: "#6f42c1" },
};

const CategoryBadge = ({ label }) => {
    const key = (label || "").toLowerCase();
    const style = categoryStyles[key] || { backgroundColor: "#e2e3e5", color: "#383d41" };
    return (
        <span style={{
            ...style,
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "500",
            textTransform: "capitalize",
            whiteSpace: "nowrap",
        }}>
            {label}
        </span>
    );
};

// ─── Call category resolver ───────────────────────────────────────────────────

const getVideoCallCategory = (call) => {
    if (hasValue(call.videoCallStatus) && call.videoCallStatus === "Missed Call") return "Missed Call";
    if (call.participants?.length > 0) {
        // Dashboard Call: initiator is a portal user (userId matches initiatorId).
        // Compare as strings since GUIDs may be serialized differently.
        const initiatorIdStr = call.initiatorId ? String(call.initiatorId) : null;
        const portalUserInitiated = initiatorIdStr && call.participants.some(
            (p) => p.userId != null && String(p.userId) === initiatorIdStr
        );
        return portalUserInitiated ? "Dashboard Call" : "Family Call";
    }
    return "Family Call";
};

// ─── Main component ───────────────────────────────────────────────────────────

const CallTranscriptsTab = ({ user, userInfo }) => {
    const [callLogs, setCallLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCall, setSelectedCall] = useState(null);
    const [activeTab, setActiveTab] = useState("transcript");
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [audioSignUrl, setAudioSignUrl] = useState(null);
    const [transcriptSignUrl, setTranscriptSignUrl] = useState(null);
    // Refs so async callbacks always read current blob-url state (avoids stale closure leak)
    const transcriptSignUrlRef = useRef(null);
    const isTranscriptBlobUrlRef = useRef(false);

    const setTranscriptState = (url, isBlob) => {
        transcriptSignUrlRef.current = url;
        isTranscriptBlobUrlRef.current = isBlob;
        setTranscriptSignUrl(url);
    };

    const revokePreviousBlobUrl = () => {
        if (isTranscriptBlobUrlRef.current && transcriptSignUrlRef.current) {
            URL.revokeObjectURL(transcriptSignUrlRef.current);
        }
    };
    const [timePreset, setTimePreset] = useState("all");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    // client-side filters
    const [typeFilter, setTypeFilter] = useState("all");
    // server-side filters / sort
    const [sortOrder, setSortOrder] = useState("desc");
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState(""); // debounced
    const [classification, setClassification] = useState("");

    // ── Signed URL loaders (video only) ──────────────────────────────────────

    const getAudioSignedUrl = async (fileName) => {
        try {
            const response = await getMediaSignUrl(fileName, "audio");
            if (response.success) {
                const audioUrls = response?.data || [];
                setAudioSignUrl(audioUrls?.[0] || null);
            } else {
                setAudioSignUrl(null);
                toast.error("Failed to load audio recording");
            }
        } catch (error) {
            console.error("Error loading audio recording:", error);
            setAudioSignUrl(null);
            toast.error("Failed to load audio recording");
        }
    };

    const getTranscriptSignedUrl = async (fileName) => {
        try {
            const response = await getMediaSignUrl(fileName, "trans");
            if (response.success) {
                const vttUrls = response?.data || [];

                if (vttUrls.length === 1) {
                    revokePreviousBlobUrl();
                    setTranscriptState(vttUrls[0], false);
                } else if (vttUrls.length > 1) {
                    try {
                        const mergedContent = await mergeVTTFiles(vttUrls);
                        const blob = new Blob([mergedContent], { type: "text/vtt" });
                        const vttBlobUrl = URL.createObjectURL(blob);
                        revokePreviousBlobUrl();
                        setTranscriptState(vttBlobUrl, true);
                    } catch (error) {
                        console.error("Error merging VTT files:", error);
                        toast.error("Failed to merge transcript files");
                    }
                } else {
                    revokePreviousBlobUrl();
                    setTranscriptState(null, false);
                }
            }
        } catch (error) {
            console.error("Error loading transcript:", error);
            toast.error("Failed to load transcript");
        }
    };

    const mergeVTTFiles = async (vttUrls) => {
        let mergedContent = 'WEBVTT\n\n';

        for (let i = 0; i < vttUrls.length; i++) {
            try {
                const response = await fetch(vttUrls[i]);
                if (!response.ok) throw new Error(`Failed to fetch VTT file ${i + 1}`);

                const content = await response.text();
                const lines = content.split('\n');
                let startIndex = 0;

                if (i === 0) {
                    for (let j = 0; j < lines.length; j++) {
                        if (lines[j].trim() === 'WEBVTT') {
                            startIndex = j + 1;
                            while (startIndex < lines.length && lines[startIndex].trim() === '') startIndex++;
                            break;
                        }
                    }
                } else {
                    for (let j = 0; j < lines.length; j++) {
                        if (lines[j].includes(' --> ')) { startIndex = j; break; }
                    }
                }

                for (let j = startIndex; j < lines.length; j++) {
                    mergedContent += lines[j] + '\n';
                }
            } catch (error) {
                console.error(`Error processing VTT file ${i + 1}:`, error);
            }
        }

        return mergedContent;
    };

    // ── Download helpers (video only) ─────────────────────────────────────────

    const triggerBrowserDownload = (blob, fileName) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    const downloadFileFromUrl = async (url, fileName) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Download failed");
            const blob = await response.blob();
            triggerBrowserDownload(blob, fileName);
        } catch (error) {
            console.error("Download error:", error);
            toast.error("Failed to download file");
        }
    };

    const handleDownloadAudio = async () => {
        if (!audioSignUrl || !selectedCall?.audioFileName) {
            toast.error("Audio file is not available");
            return;
        }
        await downloadFileFromUrl(audioSignUrl, selectedCall.audioFileName || "recording.mp4");
    };

    const handleDownloadTranscript = async () => {
        if (!transcriptSignUrl || !selectedCall?.transFileName) {
            toast.error("Transcript file is not available");
            return;
        }
        try {
            const vttContent = await resolveTranscriptTextContent(transcriptSignUrl);
            const readableText = convertVttToReadableText(vttContent, selectedCall?.participants || []);
            const textBlob = new Blob([readableText], { type: "text/plain;charset=utf-8" });
            const downloadName = (selectedCall.transFileName || "transcript.vtt")
                .replace(/\.m3u8$/i, "")
                .replace(/\.vtt$/i, "") + ".txt";
            triggerBrowserDownload(textBlob, downloadName);
        } catch (error) {
            console.error("Transcript download error:", error);
            toast.error("Failed to download transcript");
        }
    };

    // ── Data fetching ─────────────────────────────────────────────────────────

    // Transform a single CallHistoryItem from the unified endpoint into the
    // shape used by this component.
    const transformItem = (item) => {
        if (item.callType === "phone") {
            const callerNumber = item.callerNumberNormalized || item.callerNumberRaw || null;
            const receiverNumber = item.receiverNumberNormalized || item.receiverNumberRaw || null;
            return {
                id:         item.id,
                callType:   "phone",
                startTime:  item.startedAt || null,
                // If there's a resolved name, show name on top and number below.
                // If there's no name, show the number once as the primary label (no subtitle).
                fromName:   item.callerName  || callerNumber   || "Unknown",
                fromNumber: item.callerName  ? callerNumber    : null,
                toName:     item.receiverName || receiverNumber || "Unknown",
                toNumber:   item.receiverName ? receiverNumber : null,
                outcome:    item.callOutcome,
                duration:   item.durationSeconds != null
                    ? formatDurationSeconds(item.durationSeconds)
                    : null,
                // Video fields are not used for phone rows
            };
        }

        // video
        return {
            id:             item.id,
            callType:       "video",
            startTime:      item.startedAt || null,
            fromName:       item.initiator || "Unknown",
            initiatorId:    item.initiatorId || null,
            toParticipants: item.participants || [],
            videoCallStatus: item.videoCallStatus || "",
            duration:       item.durationDisplay || "-",
            // Video media files
            audioFileName:  item.audioFileName  || null,
            transFileName:  item.transFileName  || null,
            participants:   item.participants   || [],
        };
    };

    // Debounce search input — only update `search` (which triggers a re-fetch) after
    // the user stops typing for 400 ms.
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchCallLogs = useCallback(async () => {
        try {
            setLoading(true);
            setIsRefreshing(true);

            const params = { sort_order: sortOrder, page_size: 200 };
            if (search)         params.search         = search;
            if (classification) params.classification = classification;

            const result = await fetchUnifiedCallLogs(user.id, params);

            if (result.success) {
                const items = Array.isArray(result.data) ? result.data : (result.data?.data ?? []);
                const transformed = items.map(transformItem);
                setCallLogs(transformed);
            } else {
                console.error("Failed to fetch call logs:", result.error);
                toast.error("Failed to fetch call logs");
                setCallLogs([]);
            }
        } catch (error) {
            console.error("Error fetching call logs:", error);
            toast.error("Failed to fetch call logs");
            setCallLogs([]);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [user.id, sortOrder, search, classification]);

    useEffect(() => {
        fetchCallLogs();
    }, [fetchCallLogs]);

    useEffect(() => {
        return () => { revokePreviousBlobUrl(); };
    }, []);

    // ── Time filtering ────────────────────────────────────────────────────────

    const parseStartTimeToDate = (timeString) => {
        if (!timeString) return null;
        const s = timeString.endsWith("Z") ? timeString : `${timeString}Z`;
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    };

    const startOfLocalDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    const addLocalDays = (d, days) => { const x = new Date(d.getTime()); x.setDate(x.getDate() + days); return x; };

    const computeRange = () => {
        const now = new Date();
        if (timePreset === "all")        return { from: null, to: null, valid: true };
        if (timePreset === "today")      { const f = startOfLocalDay(now); return { from: f, to: addLocalDays(f, 1), valid: true }; }
        if (timePreset === "last3")      { const f = addLocalDays(startOfLocalDay(now), -3); return { from: f, to: addLocalDays(startOfLocalDay(now), 1), valid: true }; }
        if (timePreset === "last7")      { const f = addLocalDays(startOfLocalDay(now), -7); return { from: f, to: addLocalDays(startOfLocalDay(now), 1), valid: true }; }
        if (timePreset === "this_month") { const f = new Date(now.getFullYear(), now.getMonth(), 1); return { from: f, to: new Date(now.getFullYear(), now.getMonth() + 1, 1), valid: true }; }
        if (timePreset === "custom") {
            if (!customFrom || !customTo) return { from: null, to: null, valid: false };
            const f = new Date(`${customFrom}T00:00:00`);
            const t = addLocalDays(new Date(`${customTo}T00:00:00`), 1);
            if (isNaN(f.getTime()) || isNaN(t.getTime()) || f >= t) return { from: null, to: null, valid: false };
            return { from: f, to: t, valid: true };
        }
        return { from: null, to: null, valid: true };
    };

    const { from: rangeFrom, to: rangeTo, valid: isRangeValid } = useMemo(
        () => computeRange(),
        [timePreset, customFrom, customTo]
    );

    const filteredCallLogs = useMemo(() => {
        if (!callLogs?.length) return [];

        return callLogs.filter((call) => {
            // type filter
            if (typeFilter !== "all" && call.callType !== typeFilter) return false;

            // time filter
            if (timePreset === "all") return true;
            if (!isRangeValid) return false;
            const d = parseStartTimeToDate(call.startTime);
            if (!d) return false;
            if (rangeFrom && d < rangeFrom) return false;
            if (rangeTo && d >= rangeTo) return false;
            return true;
        });
    }, [callLogs, typeFilter, timePreset, isRangeValid, rangeFrom, rangeTo]);

    // Server already sorted; preserve server order for filtered subset.
    const viewLogs = useMemo(() => [...filteredCallLogs], [filteredCallLogs]);

    // ── Action handlers ───────────────────────────────────────────────────────

    const handleViewCall = async (call) => {
        setSelectedCall(call);

        if (call.audioFileName) getAudioSignedUrl(call.audioFileName);
        if (call.transFileName) getTranscriptSignedUrl(call.transFileName);

        if (call.transFileName)      setActiveTab("transcript");
        else if (call.audioFileName) setActiveTab("audio");
        else                         setActiveTab("summary");
    };

    const closeWindowHandler = () => {
        revokePreviousBlobUrl();
        setSelectedCall(null);
        setAudioSignUrl(null);
        setTranscriptState(null, false);
    };

    const handleRefresh = () => fetchCallLogs();

    // ── Formatting helpers ────────────────────────────────────────────────────

    const formatDateTime = (timeString) => {
        if (!timeString) return "Unknown Time";
        const s = timeString.endsWith("Z") ? timeString : `${timeString}Z`;
        const date = new Date(s);
        if (isNaN(date.getTime())) return "Invalid Date";
        return `${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).replace(" ", "")} on ${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    };

    // ── Excel export ──────────────────────────────────────────────────────────

    const parseUtcToLocalParts = (timeString) => {
        if (!timeString) return null;
        const s = timeString.endsWith("Z") ? timeString : `${timeString}Z`;
        const date = new Date(s);
        if (isNaN(date.getTime())) return null;
        return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hour: date.getHours(), minute: date.getMinutes(), second: date.getSeconds() };
    };

    const localPartsToExcelSerial = ({ year, month, day, hour, minute, second }) => {
        const excelEpoch = Date.UTC(1899, 11, 30);
        const utcMillis = Date.UTC(year, month - 1, day, hour, minute, second);
        return (utcMillis - excelEpoch) / 86400000;
    };

    const parseStartTimeToExcelSerial = (timeString) => {
        const parts = parseUtcToLocalParts(timeString);
        return parts ? localPartsToExcelSerial(parts) : null;
    };

    const handleExportToExcel = async (logsToExport, fileSuffix) => {
        if (!logsToExport || logsToExport.length === 0) {
            toast.error("No data to export");
            return;
        }

        try {
            const wb = new ExcelJS.Workbook();
            const ws = wb.addWorksheet("Call Logs");

            ws.columns = [
                { header: "Device name",    key: "deviceName", width: 22 },
                { header: "Serial",         key: "serial",     width: 18 },
                { header: "Date and time",  key: "dateTime",   width: 22 },
                { header: "Type",           key: "type",       width: 10 },
                { header: "From",           key: "from",       width: 22 },
                { header: "To",             key: "to",         width: 22 },
                { header: "Outcome",        key: "outcome",    width: 14 },
                { header: "Call Category",  key: "category",   width: 16 },
                { header: "Duration",       key: "duration",   width: 12 },
            ];

            logsToExport.forEach((call) => {
                const dtSerial = parseStartTimeToExcelSerial(call.startTime);

                let fromCol, toCol, outcomeCol, categoryCol;

                if (call.callType === "phone") {
                    fromCol     = call.fromName + (call.fromNumber ? ` (${call.fromNumber})` : "");
                    toCol       = call.toName   + (call.toNumber   ? ` (${call.toNumber})`   : "");
                    outcomeCol  = normalizePhoneOutcome(call.outcome);
                    categoryCol = "";
                } else {
                    fromCol     = call.fromName || "";
                    const exportInitiatorStr = call.initiatorId ? String(call.initiatorId) : null;
                    const others = (call.toParticipants || [])
                        .filter((p) => {
                            if (!hasValue(p.name)) return false;
                            if (exportInitiatorStr) {
                                if (p.deviceId != null && String(p.deviceId) === exportInitiatorStr) return false;
                                if (p.userId   != null && String(p.userId)   === exportInitiatorStr) return false;
                            }
                            return true;
                        })
                        .map((p) => p.name)
                        .join(", ");
                    toCol       = others;
                    outcomeCol  = call.videoCallStatus === "Missed Call" ? "missed" : "connected";
                    categoryCol = getVideoCallCategory(call);
                }

                ws.addRow({
                    deviceName: user?.name       || "",
                    serial:     user?.serialNo   || "",
                    dateTime:   dtSerial ?? null,
                    type:       call.callType,
                    from:       fromCol,
                    to:         toCol,
                    outcome:    outcomeCol,
                    category:   categoryCol,
                    duration:   call.duration || "",
                });
            });

            ws.getColumn(3).numFmt = "m/d/yyyy h:mm AM/PM";

            ws.eachRow((row) => {
                row.eachCell((cell) => { cell.font = { name: "DengXian", size: 11 }; });
            });

            ws.getRow(1).eachCell((cell) => {
                cell.font = { name: "DengXian", size: 11, bold: true };
            });

            const buffer = await wb.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
            saveAs(blob, `call_logs_${fileSuffix}_${timestamp}.xlsx`);
            toast.success("Call logs exported successfully");
        } catch (e) {
            console.error(e);
            toast.error("Failed to export call logs");
        }
    };

    const exportMenuOpen = Boolean(exportAnchorEl);
    const handleOpenExportMenu  = (e) => setExportAnchorEl(e.currentTarget);
    const handleCloseExportMenu = ()  => setExportAnchorEl(null);
    const handleExportAll = async () => { handleCloseExportMenu(); await handleExportToExcel(callLogs, "all"); };
    const handleExportCurrentRange = async () => {
        handleCloseExportMenu();
        if (timePreset === "custom" && !isRangeValid) { toast.error("Invalid custom date range"); return; }
        await handleExportToExcel(filteredCallLogs, "current_range");
    };

    // ── Render helpers ────────────────────────────────────────────────────────

    const renderFromCell = (call) => {
        if (call.callType === "phone") {
            return (
                <div>
                    <div style={{ fontWeight: "500" }}>{call.fromName}</div>
                    {call.fromNumber && <div style={{ fontSize: "12px", color: "#6c757d" }}>{call.fromNumber}</div>}
                </div>
            );
        }
        // video
        return <div style={{ fontWeight: "500" }}>{call.fromName}</div>;
    };

    const renderToCell = (call) => {
        if (call.callType === "phone") {
            return (
                <div>
                    <div style={{ fontWeight: "500" }}>{call.toName}</div>
                    {call.toNumber && <div style={{ fontSize: "12px", color: "#6c757d" }}>{call.toNumber}</div>}
                </div>
            );
        }
        // video: list all non-initiator participants
        const initiatorIdStr = call.initiatorId ? String(call.initiatorId) : null;
        const others = (call.toParticipants || [])
            .filter((p) => {
                if (!hasValue(p.name)) return false;
                if (initiatorIdStr) {
                    if (p.deviceId != null && String(p.deviceId) === initiatorIdStr) return false;
                    if (p.userId   != null && String(p.userId)   === initiatorIdStr) return false;
                }
                return true;
            })
            .map((p) => p.name)
            .join(", ");
        return <div>{others || "—"}</div>;
    };

    const renderCategoryCell = (call) => {
        if (call.callType === "phone") {
            const label = normalizePhoneOutcome(call.outcome);
            return <CategoryBadge label={label.charAt(0).toUpperCase() + label.slice(1)} />;
        }
        return <CategoryBadge label={getVideoCallCategory(call)} />;
    };

    const renderActionsCell = (call) => {
        if (call.callType === "phone") return null;

        const hasTranscript = hasValue(call.transFileName);
        const hasAudio      = hasValue(call.audioFileName);

        if (!hasTranscript && !hasAudio) return null;

        const label = hasTranscript && hasAudio
            ? "View transcript/recording"
            : hasTranscript ? "View transcript" : "View recording";

        return (
            <Button onClick={() => handleViewCall(call)} variant="link">
                {label}
            </Button>
        );
    };

    if (loading && !isRefreshing) {
        return <div style={{ padding: "20px" }}>Loading...</div>;
    }

    return (
        <div className="row">
            <div className="col">
                {/* ── Header bar ─────────────────────────────────────────── */}
                <div className="form-group text-center" style={{ position: "relative" }}>
                    <h3>CALL LOGS</h3>

                    <div style={{ position: "absolute", left: "0", top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        {/* Search */}
                        <input
                            type="text"
                            placeholder="Search name or number…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "14px", width: "180px" }}
                        />

                        {/* Type filter */}
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "14px" }}
                        >
                            <option value="all">All types</option>
                            <option value="phone">Phone</option>
                            <option value="video">Video</option>
                        </select>

                        {/* Time range filter */}
                        <select
                            value={timePreset}
                            onChange={(e) => setTimePreset(e.target.value)}
                            style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "14px" }}
                        >
                            <option value="today">Today</option>
                            <option value="last3">Past 3 days</option>
                            <option value="last7">Past 7 days</option>
                            <option value="this_month">This month</option>
                            <option value="custom">Custom range</option>
                            <option value="all">All time</option>
                        </select>

                        {timePreset === "custom" && (
                            <>
                                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "14px" }} />
                                <span style={{ fontSize: "14px", color: "#6c757d" }}>to</span>
                                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "14px" }} />
                            </>
                        )}
                    </div>

                    <button
                        onClick={handleOpenExportMenu}
                        style={{ position: "absolute", right: "0", top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", backgroundColor: "#007bff", color: "white", border: "1px solid #007bff", borderRadius: "4px", cursor: "pointer", fontSize: "14px", fontWeight: "500" }}
                    >
                        <FileDownloadIcon style={{ fontSize: "18px" }} />
                        <span>Export</span>
                    </button>

                    <Menu anchorEl={exportAnchorEl} open={exportMenuOpen} onClose={handleCloseExportMenu}>
                        <MenuItem onClick={handleExportAll}>Export all call logs</MenuItem>
                        <MenuItem onClick={handleExportCurrentRange}>Export current time range</MenuItem>
                    </Menu>
                </div>

                {/* ── Table ──────────────────────────────────────────────── */}
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th width="15%" style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }} onClick={() => setSortOrder(s => s === "desc" ? "asc" : "desc")}>
                                Date and time {sortOrder === "desc" ? "▼" : "▲"}
                            </th>
                            <th width="8%" style={{ textAlign: "center" }}>Type</th>
                            <th width="16%">From</th>
                            <th width="16%">To</th>
                            <th width="14%">Call Category</th>
                            <th width="9%">Duration</th>
                            <th width="10%">Actions</th>
                            <th width="4%">
                                <Button onClick={handleRefresh} variant="link">
                                    <RefreshIcon style={{ fontSize: "20px", animation: isRefreshing ? "spin 1s linear infinite" : "none" }} />
                                    <style jsx>{`
                                        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                                    `}</style>
                                </Button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {viewLogs.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ padding: "40px", textAlign: "center", color: "#6c757d", backgroundColor: "#f8f9fa" }}>
                                    {callLogs.length === 0
                                        ? "No call logs available."
                                        : timePreset === "custom" && !isRangeValid
                                        ? "Invalid custom date range."
                                        : "No call logs in this time range."}
                                </td>
                            </tr>
                        ) : (
                            viewLogs.map((call) => (
                                <tr key={call.id} style={{ backgroundColor: "white" }}>
                                    <td style={{ verticalAlign: "middle" }}>{formatDateTime(call.startTime)}</td>
                                    <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "5px", justifyContent: "center" }}>
                                            {call.callType === "phone"
                                                ? <PhoneIcon style={{ fontSize: "16px", color: "#0c5460" }} />
                                                : <VideocamIcon style={{ fontSize: "16px", color: "#6f42c1" }} />
                                            }
                                            <span style={{ fontSize: "12px", color: call.callType === "phone" ? "#0c5460" : "#6f42c1", fontWeight: "500" }}>
                                                {call.callType === "phone" ? "Phone" : "Video"}
                                            </span>
                                        </div>
                                    </td>
                                    <td style={{ verticalAlign: "middle" }}>{renderFromCell(call)}</td>
                                    <td style={{ verticalAlign: "middle" }}>{renderToCell(call)}</td>
                                    <td style={{ verticalAlign: "middle" }}>{renderCategoryCell(call)}</td>
                                    <td style={{ verticalAlign: "middle" }}>{call.duration || "—"}</td>
                                    <td style={{ verticalAlign: "middle" }}>{renderActionsCell(call)}</td>
                                    <td></td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* ── Video call detail modal ─────────────────────────────── */}
                <Modal open={!!selectedCall} onClose={closeWindowHandler} aria-labelledby="call-modal">
                    <Box sx={{ position: "absolute", top: "50%", left: "55%", transform: "translate(-50%, -50%)", flexDirection: "column", width: "70%", maxWidth: "1200px", maxHeight: "90vh", bgcolor: "white", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)", display: "flex" }}>
                        {/* Header */}
                        <div style={{ padding: "15px 20px" }}>
                            <table className="table" style={{ margin: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: "250px" }}>Date and time</th>
                                        <th>From</th>
                                        <th>Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedCall && (
                                        <tr style={{ transition: "background-color 0.3s", borderColor: "transparent !important" }}>
                                            <td style={{ verticalAlign: "middle" }}>{formatDateTime(selectedCall.startTime)}</td>
                                            <td style={{ verticalAlign: "middle" }}>{selectedCall.fromName}</td>
                                            <td style={{ verticalAlign: "middle" }}>{selectedCall.duration}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Tab navigation */}
                        <div style={{ padding: "0 20px", borderBottom: "1px solid #E9ECEF", display: "flex", gap: "0" }}>
                            {selectedCall?.transFileName && (
                                <button
                                    onClick={() => setActiveTab("transcript")}
                                    style={{ padding: "10px 20px", backgroundColor: activeTab === "transcript" ? "#f8f9fa" : "transparent", color: activeTab === "transcript" ? "#495057" : "#6c757d", border: "none", borderBottom: activeTab === "transcript" ? "2px solid #007bff" : "2px solid transparent", cursor: "pointer", fontSize: "14px", fontWeight: activeTab === "transcript" ? "bold" : "normal" }}
                                >
                                    Transcript
                                </button>
                            )}
                            {selectedCall?.audioFileName && (
                                <button
                                    onClick={() => setActiveTab("audio")}
                                    style={{ padding: "10px 20px", backgroundColor: activeTab === "audio" ? "#f8f9fa" : "transparent", color: activeTab === "audio" ? "#495057" : "#6c757d", border: "none", borderBottom: activeTab === "audio" ? "2px solid #007bff" : "2px solid transparent", cursor: "pointer", fontSize: "14px", fontWeight: activeTab === "audio" ? "bold" : "normal" }}
                                >
                                    Audio Recording
                                </button>
                            )}
                        </div>

                        {/* Content */}
                        <div style={{ flexGrow: 1, overflowY: "auto", maxHeight: "calc(90vh - 300px)" }}>
                            {activeTab === "audio" && selectedCall?.audioFileName && (
                                <div style={{ padding: "15px 20px" }}>
                                    <h4 style={{ marginTop: 0, marginBottom: "15px" }}>CALL RECORDING</h4>
                                    <div>
                                        <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "6px", border: "1px solid #e9ecef" }}>
                                            <div style={{ marginBottom: "10px", fontSize: "14px", color: "#6c757d" }}><strong>Recording Details:</strong></div>
                                            <div style={{ fontSize: "14px", marginBottom: "5px" }}><strong>Date:</strong> {formatDateTime(selectedCall.startTime)}</div>
                                            <div style={{ fontSize: "14px", marginBottom: "5px" }}><strong>File:</strong> {selectedCall.audioFileName}</div>
                                            {selectedCall.participants && (
                                                <div style={{ fontSize: "14px", marginBottom: "5px" }}>
                                                    <strong>Participants:</strong> {selectedCall.participants.map((p) => p.name).join(", ")}
                                                </div>
                                            )}
                                        </div>

                                        <div style={{ padding: "20px", backgroundColor: "#fff", border: "2px solid #e9ecef", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                                                <strong>Audio Player</strong>
                                                <Button onClick={handleDownloadAudio} variant="link">
                                                    <FileDownloadIcon style={{ fontSize: "18px", marginRight: "6px" }} />
                                                    Download MP4
                                                </Button>
                                            </div>
                                            <audio key={`modal-audio-${selectedCall.id}`} controls preload="metadata" style={{ width: "100%" }} src={audioSignUrl || ""}>
                                                Your browser does not support the audio element.
                                            </audio>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "transcript" && selectedCall?.transFileName && (
                                <div style={{ padding: "15px 20px" }}>
                                    <h4 style={{ marginTop: 0, marginBottom: "30px", fontSize: "18px", fontWeight: "bold", color: "#333", letterSpacing: "0.5px", display: "flex", alignItems: "center" }}>
                                        CALL TRANSCRIPT{" "}
                                        <ContentCopy
                                            onClick={() => {
                                                toast.success("Copied");
                                                navigator.clipboard.writeText(document.getElementById("divTranscriptionData")?.innerText || "");
                                            }}
                                            sx={{ color: "black", marginRight: "10px", cursor: "pointer", marginLeft: "8px" }}
                                        />
                                        <FileDownloadIcon onClick={handleDownloadTranscript} sx={{ color: "black", cursor: "pointer" }} />
                                    </h4>
                                    <ConversationDisplay vttUrl={transcriptSignUrl} participants={selectedCall.participants} />
                                </div>
                            )}
                        </div>

                        {/* Close */}
                        <div style={{ textAlign: "center", padding: "15px", borderTop: "1px solid #E2E2E2" }}>
                            <Button onClick={closeWindowHandler} variant="action-popup">Close window</Button>
                        </div>
                    </Box>
                </Modal>
            </div>
        </div>
    );
};

// ─── Duration formatter ───────────────────────────────────────────────────────

function formatDurationSeconds(seconds) {
    if (seconds == null || seconds <= 0) return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default CallTranscriptsTab;